import { useLocalStorage } from './useLocalStorage';
import { useState, useEffect } from 'react';

// Achievement definitions
export const ACHIEVEMENTS = {
  FIRST_CARD: {
    id: 'first_card',
    name: 'First Steps',
    description: 'Review your first card',
    icon: '🎯',
    requirement: 1,
    type: 'cards_reviewed'
  },
  TEN_CARDS: {
    id: 'ten_cards',
    name: 'Getting Started',
    description: 'Review 10 cards',
    icon: '📚',
    requirement: 10,
    type: 'cards_reviewed'
  },
  FIFTY_CARDS: {
    id: 'fifty_cards',
    name: 'Dedicated Learner',
    description: 'Review 50 cards',
    icon: '🌟',
    requirement: 50,
    type: 'cards_reviewed'
  },
  HUNDRED_CARDS: {
    id: 'hundred_cards',
    name: 'Century Club',
    description: 'Review 100 cards',
    icon: '💯',
    requirement: 100,
    type: 'cards_reviewed'
  },
  FIVE_HUNDRED_CARDS: {
    id: 'five_hundred_cards',
    name: 'Scholar',
    description: 'Review 500 cards',
    icon: '🎓',
    requirement: 500,
    type: 'cards_reviewed'
  },
  THOUSAND_CARDS: {
    id: 'thousand_cards',
    name: 'Master',
    description: 'Review 1000 cards',
    icon: '👑',
    requirement: 1000,
    type: 'cards_reviewed'
  },
  FIRST_STREAK: {
    id: 'first_streak',
    name: 'Consistency',
    description: 'Maintain a 3-day streak',
    icon: '🔥',
    requirement: 3,
    type: 'streak'
  },
  WEEK_STREAK: {
    id: 'week_streak',
    name: 'Week Warrior',
    description: 'Maintain a 7-day streak',
    icon: '⚡',
    requirement: 7,
    type: 'streak'
  },
  MONTH_STREAK: {
    id: 'month_streak',
    name: 'Monthly Master',
    description: 'Maintain a 30-day streak',
    icon: '🏆',
    requirement: 30,
    type: 'streak'
  },
  PERFECT_SCORE: {
    id: 'perfect_score',
    name: 'Perfectionist',
    description: 'Get 10 cards correct in a row',
    icon: '✨',
    requirement: 10,
    type: 'correct_streak'
  },
  EARLY_BIRD: {
    id: 'early_bird',
    name: 'Early Bird',
    description: 'Study before 8 AM',
    icon: '🌅',
    requirement: 1,
    type: 'time_based'
  },
  NIGHT_OWL: {
    id: 'night_owl',
    name: 'Night Owl',
    description: 'Study after 10 PM',
    icon: '🦉',
    requirement: 1,
    type: 'time_based'
  },
  DAILY_GOAL: {
    id: 'daily_goal',
    name: 'Goal Achiever',
    description: 'Complete your daily goal',
    icon: '🎯',
    requirement: 1,
    type: 'goal'
  },
  WEEKLY_GOAL: {
    id: 'weekly_goal',
    name: 'Weekly Champion',
    description: 'Complete your weekly goal',
    icon: '🏅',
    requirement: 1,
    type: 'goal'
  },
  DECK_MASTER: {
    id: 'deck_master',
    name: 'Deck Master',
    description: 'Complete all cards in a deck',
    icon: '📖',
    requirement: 1,
    type: 'deck_completion'
  }
};

export const useAchievements = () => {
  const [achievementData, setAchievementData] = useLocalStorage('achievements', {
    unlockedAchievements: [],
    totalCardsReviewed: 0,
    currentCorrectStreak: 0,
    bestCorrectStreak: 0,
    newAchievements: [] // Recently unlocked achievements to show notification
  });

  const [newlyUnlocked, setNewlyUnlocked] = useState([]);

  const checkAchievement = (achievementId, currentValue) => {
    const achievement = Object.values(ACHIEVEMENTS).find(a => a.id === achievementId);
    if (!achievement) return false;

    const isUnlocked = achievementData.unlockedAchievements.includes(achievementId);
    if (isUnlocked) return false;

    return currentValue >= achievement.requirement;
  };

  const unlockAchievement = (achievementId) => {
    if (achievementData.unlockedAchievements.includes(achievementId)) return;

    const newUnlocked = [...achievementData.unlockedAchievements, achievementId];
    setAchievementData({
      ...achievementData,
      unlockedAchievements: newUnlocked
    });

    // Add to newly unlocked for notification
    const achievement = Object.values(ACHIEVEMENTS).find(a => a.id === achievementId);
    if (achievement) {
      setNewlyUnlocked([achievement]);
    }
  };

  const checkAndUnlockAchievements = ({
    cardsReviewed,
    currentStreak,
    dailyGoalMet,
    weeklyGoalMet,
    isCorrect
  }) => {
    const unlocked = [];

    // Update correct streak
    let newCorrectStreak = achievementData.currentCorrectStreak;
    if (isCorrect !== undefined) {
      newCorrectStreak = isCorrect ? newCorrectStreak + 1 : 0;
    }

    const newBestStreak = Math.max(newCorrectStreak, achievementData.bestCorrectStreak);
    const newTotalReviewed = achievementData.totalCardsReviewed + (cardsReviewed || 0);

    // Check card review achievements
    Object.values(ACHIEVEMENTS).forEach(achievement => {
      if (achievementData.unlockedAchievements.includes(achievement.id)) return;

      let shouldUnlock = false;

      switch (achievement.type) {
        case 'cards_reviewed':
          shouldUnlock = newTotalReviewed >= achievement.requirement;
          break;
        case 'streak':
          shouldUnlock = currentStreak >= achievement.requirement;
          break;
        case 'correct_streak':
          shouldUnlock = newBestStreak >= achievement.requirement;
          break;
        case 'goal':
          if (achievement.id === 'daily_goal') {
            shouldUnlock = dailyGoalMet;
          } else if (achievement.id === 'weekly_goal') {
            shouldUnlock = weeklyGoalMet;
          }
          break;
        case 'time_based':
          const hour = new Date().getHours();
          if (achievement.id === 'early_bird') {
            shouldUnlock = hour < 8;
          } else if (achievement.id === 'night_owl') {
            shouldUnlock = hour >= 22;
          }
          break;
      }

      if (shouldUnlock) {
        unlocked.push(achievement.id);
      }
    });

    if (unlocked.length > 0 || cardsReviewed || isCorrect !== undefined) {
      setAchievementData({
        ...achievementData,
        unlockedAchievements: [...achievementData.unlockedAchievements, ...unlocked],
        totalCardsReviewed: newTotalReviewed,
        currentCorrectStreak: newCorrectStreak,
        bestCorrectStreak: newBestStreak
      });

      // Set newly unlocked achievements for notification
      if (unlocked.length > 0) {
        const newAchievements = unlocked.map(id =>
          Object.values(ACHIEVEMENTS).find(a => a.id === id)
        );
        setNewlyUnlocked(newAchievements);
      }
    }
  };

  const clearNewAchievements = () => {
    setNewlyUnlocked([]);
  };

  const getProgress = (achievementId) => {
    const achievement = Object.values(ACHIEVEMENTS).find(a => a.id === achievementId);
    if (!achievement) return 0;

    switch (achievement.type) {
      case 'cards_reviewed':
        return Math.min((achievementData.totalCardsReviewed / achievement.requirement) * 100, 100);
      case 'correct_streak':
        return Math.min((achievementData.bestCorrectStreak / achievement.requirement) * 100, 100);
      default:
        return achievementData.unlockedAchievements.includes(achievementId) ? 100 : 0;
    }
  };

  const unlockedCount = achievementData.unlockedAchievements.length;
  const totalCount = Object.keys(ACHIEVEMENTS).length;

  return {
    achievements: ACHIEVEMENTS,
    unlockedAchievements: achievementData.unlockedAchievements,
    newlyUnlocked,
    unlockedCount,
    totalCount,
    totalCardsReviewed: achievementData.totalCardsReviewed,
    currentCorrectStreak: achievementData.currentCorrectStreak,
    bestCorrectStreak: achievementData.bestCorrectStreak,
    checkAndUnlockAchievements,
    clearNewAchievements,
    getProgress
  };
};
